import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link2Off, Users } from 'lucide-react';
import type { InvitePreview } from '@spawnwire/shared';
import { Wordmark } from '../../components/Logo';
import { Avatar, Button, EmptyState, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { useStore } from '../../store/store';
import { SpaceIcon } from '../shell/SpaceIcon';

export function InvitePage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const alreadyMember = useStore((s) => (preview ? !!s.spaces[preview.space.id] : false));

  useEffect(() => {
    http
      .get<InvitePreview>(`/invites/${encodeURIComponent(code)}`)
      .then(setPreview)
      .catch((err) => setError(errorMessage(err)));
  }, [code]);

  async function join() {
    setJoining(true);
    try {
      const res = await http.post<{ spaceId: string; channelId: string }>(`/invites/${encodeURIComponent(code)}`);
      navigate(`/app/${res.spaceId}/${res.channelId}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setJoining(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-rail p-4">
      <div className="animate-pop w-full max-w-md rounded-2xl border border-line bg-main p-8 text-center shadow-pop">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        {error ? (
          <EmptyState icon={Link2Off} title="This invite can't be used" body={`${error} It may have expired, been revoked or reached its use limit.`} action={<Button onClick={() => navigate('/app/@me')}>Go home</Button>} />
        ) : !preview ? (
          <Spinner className="mx-auto size-8" />
        ) : (
          <div className="space-y-5">
            {preview.inviter && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted">
                <Avatar user={preview.inviter} size={20} />
                <span>
                  <span className="font-medium text-fg">{preview.inviter.displayName}</span> invited you to join
                </span>
              </div>
            )}
            <div className="flex justify-center">
              <SpaceIcon name={preview.space.name} iconUrl={preview.space.iconUrl} size={80} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-fg">{preview.space.name}</h1>
              {preview.space.description && <p className="mt-2 text-sm text-muted">{preview.space.description}</p>}
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted">
                <Users className="size-4" aria-hidden /> {preview.space.memberCount} {preview.space.memberCount === 1 ? 'member' : 'members'}
              </p>
            </div>
            {alreadyMember ? (
              <Button size="lg" className="w-full" onClick={() => navigate(`/app/${preview.space.id}`)}>
                You're already a member. Open it
              </Button>
            ) : (
              <Button size="lg" className="w-full" loading={joining} onClick={join}>
                Accept invite
              </Button>
            )}
            <button type="button" className="text-sm text-muted hover:text-fg" onClick={() => navigate('/app/@me')}>
              No thanks
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
